import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

export function stopTextEditingPropagation(
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
): void {
    if (!(e.metaKey || e.ctrlKey)) return;

    const k = e.key.toLowerCase();

    if (!["a", "c", "v", "x", "z", "y"].includes(k)) return;

    e.stopPropagation();

    if (k === "a") {
        e.currentTarget.select();
    }
}

export function FormSectionLabel({
    className,
    children,
}: {
    className?: string;
    children: React.ReactNode;
}) {
    return (
        <p
            className={cn(
                "mt-4 mb-1 font-medium text-sm text-zinc-900 leading-label",
                className,
            )}
        >
            {children}
        </p>
    );
}

export function FormFieldLabel({
    htmlFor,
    children,
    optional,
    required,
    className,
}: {
    htmlFor?: string;
    children: React.ReactNode;
    optional?: boolean;
    required?: boolean;
    className?: string;
}) {
    return (
        <label
            htmlFor={htmlFor}
            className={cn(
                "flex select-none items-center gap-1 font-normal text-xs text-zinc-500 leading-4",
                className,
            )}
        >
            {children}
            {required && (
                <span className="text-red-500" aria-hidden>
                    *
                </span>
            )}
            {optional && <span className="font-normal text-zinc-400">(optional)</span>}
        </label>
    );
}

export function FormFieldHint({
    children,
    className,
}: {
    children: React.ReactNode;
    className?: string;
}) {
    return (
        <p className={cn("font-normal text-xs text-zinc-400 leading-4", className)}>
            {children}
        </p>
    );
}

export function FormFieldError({
    children,
    className,
}: {
    children: React.ReactNode;
    className?: string;
}) {
    return (
        <p
            role="alert"
            className={cn("font-normal text-red-600 text-xs leading-4", className)}
        >
            {children}
        </p>
    );
}

export function FormField({
    label,
    htmlFor,
    hint,
    error,
    optional,
    required,
    className,
    children,
}: {
    label: string;
    htmlFor?: string;
    hint?: string;
    error?: string;
    optional?: boolean;
    required?: boolean;
    className?: string;
    children: React.ReactNode;
}) {
    return (
        <div className={cn("flex flex-col gap-1.5", className)}>
            <FormFieldLabel htmlFor={htmlFor} optional={optional} required={required}>
                {label}
            </FormFieldLabel>
            {children}
            {hint && !error && <FormFieldHint>{hint}</FormFieldHint>}
            {error && <FormFieldError>{error}</FormFieldError>}
        </div>
    );
}

export function FormInput({
    onKeyDown,
    className,
    ...props
}: React.ComponentProps<typeof Input>) {
    return (
        <Input
            className={cn(
                "text-sm [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
                className,
            )}
            onKeyDown={(e) => {
                stopTextEditingPropagation(e);
                if (e.key === "Enter" && !e.currentTarget.form) {
                    e.preventDefault();
                    e.currentTarget.blur();
                }
                onKeyDown?.(e);
            }}
            {...props}
        />
    );
}

export function FormTextarea({
    onKeyDown,
    className,
    ...props
}: React.ComponentProps<typeof Textarea>) {
    return (
        <Textarea
            className={cn("text-sm", className)}
            onKeyDown={(e) => {
                stopTextEditingPropagation(e);
                onKeyDown?.(e);
            }}
            {...props}
        />
    );
}

export function FormFieldRow({
    label,
    htmlFor,
    className,
    children,
}: {
    label: string;
    htmlFor?: string;
    className?: string;
    children: React.ReactNode;
}) {
    return (
        <div
            className={cn(
                "grid grid-cols-[4rem_1fr] items-start gap-1 py-1",
                className,
            )}
        >
            <label
                htmlFor={htmlFor}
                className="flex h-9 select-none items-center font-normal text-xs text-zinc-500 leading-4"
            >
                {label}
            </label>
            <div className="min-w-0">{children}</div>
        </div>
    );
}
